/**
 * The two AI agents behind the Sales section, named (2026-06-20). The user
 * wants them clearly identified everywhere and an at-a-glance "which one is
 * working now?" — this module is the single home for their NAMES + the pure
 * work-state derivation, so the header strip, the mode toggle, and the Status
 * two-agent panel all say the same thing.
 *
 *   • Lead Generator — the signal-driven lead agent. Runs in "leads"/"both"
 *     mode. Its leads (plus the Harvester's promoted ones) live on the Leads tab.
 *   • Harvester      — the contact harvester. Runs in "contacts"/"both" mode.
 *     Its raw contacts live on the Harvester tab; promoted orgs become Leads.
 *
 * NOTE: additive only — this does NOT rename the "Sales" section, remove tabs,
 * or touch permissions. It just makes the two agents legible.
 */
import type { SalesAgentMode } from "@xop/dashboard-shared";

export const LEAD_GENERATOR_NAME = "Lead Generator";
export const HARVESTER_NAME = "Harvester";

export type SalesAgentTrack = "lead_generator" | "harvester";

export type SalesAgentWork = "working" | "idle" | "standing_by" | "paused" | "resting";

/** Is this agent's track switched on by the current mode? */
export function trackActive(track: SalesAgentTrack, mode: SalesAgentMode): boolean {
  return track === "harvester"
    ? mode === "contacts" || mode === "both"
    : mode === "leads" || mode === "both";
}

/**
 * One plain word for "is this agent working right now?". Priority:
 *   1. a global pause stops BOTH agents → "paused";
 *   2. an agent whose track isn't selected → "standing_by";
 *   3. EITHER agent, on a spent daily budget → "resting". Every sales-AI bot
 *      (Lead Generator + Harvester + promotion) shares ONE daily budget
 *      (2026-06-21), so when it's spent they ALL rest together until the reset.
 *   4. a selected, funded agent that is NOT actually doing anything recently
 *      (`stale` — e.g. it finished its work and has nothing queued, or it's
 *      wedged) → "idle". This is the honesty guard: being switched ON is NOT
 *      the same as working. Only claim "working" when there's recent activity.
 *   5. otherwise → "working".
 * `budgetExhausted` / `stale` are optional — a surface that can't measure them
 * simply never shows that state (it stays optimistic).
 */
export function agentWork(
  track: SalesAgentTrack,
  mode: SalesAgentMode,
  opts: { isPaused: boolean; budgetExhausted?: boolean; stale?: boolean },
): SalesAgentWork {
  if (opts.isPaused) return "paused";
  if (!trackActive(track, mode)) return "standing_by";
  // ONE shared daily budget (2026-06-21): when it's spent, BOTH agents rest.
  if (opts.budgetExhausted) return "resting";
  if (opts.stale) return "idle";
  return "working";
}

export interface AgentWorkMeta {
  label: string;
  /** MUI palette key for the chip + status dot. */
  tone: "success" | "warning" | "info" | "default";
  /** Whether the dot should pulse (only while actually working). */
  live: boolean;
  /** Tooltip — the plain-language "why". */
  hint: string;
}

export function workMeta(work: SalesAgentWork): AgentWorkMeta {
  switch (work) {
    case "working":
      return {
        label: "Working now",
        tone: "success",
        live: true,
        hint: "Actively searching right now.",
      };
    case "idle":
      return {
        label: "Idle — no recent activity",
        tone: "warning",
        live: false,
        hint: "Switched on, but hasn't done anything lately — it may have caught up on its work, or be waiting. (The harvester re-sweeps so it shouldn't stay idle.)",
      };
    case "resting":
      return {
        label: "Resting (daily budget spent)",
        tone: "info",
        live: false,
        hint: "It's spent the shared daily budget — it'll pick up again after the morning reset.",
      };
    case "paused":
      return {
        label: "Paused",
        tone: "warning",
        live: false,
        hint: "An operator paused the agents. Press Resume to start them.",
      };
    case "standing_by":
      return {
        label: "Standing by",
        tone: "default",
        live: false,
        hint: "Switched off right now. Turn it on with the agent switch up top.",
      };
  }
}
